import React, { useMemo } from 'react';
import { FileTextIcon, Loader2Icon, MessageSquareTextIcon, ReceiptTextIcon, WrenchIcon } from 'lucide-react';
import { useAdminData } from '../../contexts/AdminDataContext';
import { KpiCards, type Kpi } from '../../components/admin/overview/KpiCards';
import { ActiveCasesPanel } from '../../components/admin/overview/ActiveCasesPanel';
import { LatestEnquiriesPanel } from '../../components/admin/overview/LatestEnquiriesPanel';
import { UpcomingPanel } from '../../components/admin/overview/UpcomingPanel';
import { InvoicesPanel } from '../../components/admin/overview/InvoicesPanel';
import { adminHeroImage } from '../../data/adminNav';
import { owner } from '../../data/site';
import { byPlannedDate } from '../../utils/format';
import { effectiveStatus } from '../../utils/quote';
import { dayDiff, formatKr, greeting, todayLong } from '../../utils/adminDates';
import type { CaseStatus } from '../../types/admin';
import type { Invoice } from '../../types/invoice';
const ACTIVE: CaseStatus[] = ['Kontaktet', 'Tilbud sendt', 'Accepteret', 'Planlagt', 'I gang'];
export function Overview() {
  const {
    cases,
    enquiries,
    quotes,
    invoices,
    dataLoading
  } = useAdminData();
  const data = useMemo(() => {
    const newEnquiries = enquiries.filter(item => item.status === 'Ny henvendelse');
    const active = cases.filter(item => ACTIVE.includes(item.status));
    const awaitingQuotes = quotes.filter(quote => effectiveStatus(quote) === 'Sendt');
    const unpaid = invoices.filter(invoice => invoice.status === 'Sendt');
    const unpaidTotal = unpaid.reduce((sum, invoice) => sum + (invoice.total || 0), 0);
    const activeSorted = [...active].sort((a, b) => {
      // "I gang" først, derefter efter dato.
      if (a.status === 'I gang' && b.status !== 'I gang') return -1;
      if (b.status === 'I gang' && a.status !== 'I gang') return 1;
      return byPlannedDate(a, b);
    });
    const upcoming = cases.filter(item => item.plannedDate && !['Færdig', 'Faktureret'].includes(item.status) && (dayDiff(item.plannedDate) ?? -1) >= 0).sort(byPlannedDate);
    const latestEnquiries = [...enquiries].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const latestInvoices = [...invoices].sort((a, b) => (b.invoiceDate || b.createdAt).localeCompare(a.invoiceDate || a.createdAt));
    return {
      newEnquiries,
      active,
      awaitingQuotes,
      unpaid,
      unpaidTotal,
      activeSorted,
      upcoming,
      latestEnquiries,
      latestInvoices
    };
  }, [cases, enquiries, quotes, invoices]);
  const kpis: Kpi[] = [{
    label: 'Nye henvendelser',
    value: String(data.newEnquiries.length),
    to: '/admin/henvendelser',
    icon: MessageSquareTextIcon,
    attention: data.newEnquiries.length > 0
  }, {
    label: 'Aktive sager',
    value: String(data.active.length),
    to: '/admin/sager',
    icon: WrenchIcon
  }, {
    label: 'Tilbud afventer',
    value: String(data.awaitingQuotes.length),
    to: '/admin/tilbud',
    icon: FileTextIcon
  }, {
    label: 'Ubetalte fakturaer',
    value: formatKr(data.unpaidTotal),
    to: '/admin/fakturaer',
    icon: ReceiptTextIcon,
    attention: data.unpaid.length > 0
  }];
  function invoiceTitle(invoice: Invoice): string {
    const linked = invoice.caseId ? cases.find(item => item.id === invoice.caseId) : undefined;
    const what = linked?.title || invoice.lines[0]?.description || invoice.reference;
    return [what, invoice.customerName].filter(Boolean).join(' – ');
  }
  const firstName = owner.name.split(' ')[0];
  return <div className="px-4 py-6 md:px-8 md:py-8" data-id="element-1534" data-mp="2lord6jtb430b">
      {/* Introduktion med stemningsbillede */}
      <section className="relative overflow-hidden rounded-card" data-id="element-1535" data-mp="2gs0v5qxxyxc9">
        <img src={adminHeroImage} alt="" className="absolute inset-y-0 right-0 hidden h-full w-[62%] object-cover md:block" data-id="element-1536" data-mp="rbrp02av20hf" />
        <div className="absolute inset-0 hidden md:block" style={{
        // Toner billedet ud mod venstre, så teksten altid står på mørk baggrund.
        background: 'linear-gradient(90deg, rgb(var(--c-canvas)) 38%, rgb(var(--c-canvas) / 0.7) 60%, rgb(var(--c-canvas) / 0.1))'
      }} aria-hidden="true" data-id="element-1537" data-mp="mf1ctq26fp1a" />
        <div className="relative flex items-end justify-between gap-6 py-2 md:min-h-[200px] md:py-6" data-id="element-1538" data-mp="3njvny7hv9t9c">
          <div data-id="element-1539" data-mp="2qwhbsmj8wih4">
            <h1 className="font-display text-[clamp(2rem,5vw,3rem)] font-bold leading-none tracking-tight text-ink" data-id="element-1540" data-mp="2qsthhl5z7s1c">
              Overblik
            </h1>
            <p className="mt-2 text-[15px] text-ink-soft" data-id="element-1541" data-mp="2zvclw9peucpp">{todayLong()}</p>
            <p className="mt-6 font-display text-xl font-bold tracking-tight text-ink md:text-2xl" data-id="element-1542" data-mp="34m2oxgad63h8">
              {greeting()} {firstName} <span aria-hidden="true" data-id="element-1543" data-mp="283dg6vmgvvjv">👋</span>
            </p>
            <p className="mt-1 text-[15px] text-ink-soft" data-id="element-1544" data-mp="2c60ad36m1sk8">Her er et overblik over din virksomhed i dag.</p>
          </div>
          <blockquote className="hidden max-w-[15rem] border-l border-accent/70 pl-4 lg:block" data-id="element-1545" data-mp="24n0qyxj5g10v">
            <p className="font-serif text-lg leading-snug text-ink" data-id="element-1546" data-mp="1zmwdzhhg9ofe">Kvalitetsarbejde skaber langvarige relationer.</p>
            <footer className="mt-3 text-[11px] font-semibold tracking-[0.2em] text-accent" data-id="element-1547" data-mp="1vkrlim2qmxuc">LYKKEVÆRK</footer>
          </blockquote>
        </div>
      </section>

      {dataLoading && cases.length === 0 && <p className="mt-6 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-1548" data-mp="1loer1lzdcfyj">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1549" data-mp="1gwoagy9u8vyo" />
          Henter data …
        </p>}

      <div className="mt-6" data-id="element-1550" data-mp="1bymrsegbqynq">
        <KpiCards items={kpis} data-id="element-1551" data-mp="1bvqt2nwnk5y8" />
      </div>

      <div className="mt-4 grid gap-4 md:mt-5 md:gap-5 xl:grid-cols-2" data-id="element-1552" data-mp="17x1bej4l028s">
        <ActiveCasesPanel cases={data.activeSorted.slice(0, 4)} data-id="element-1553" data-mp="12x5ef3uaxb2q" />
        <LatestEnquiriesPanel enquiries={data.latestEnquiries.slice(0, 4)} data-id="element-1554" data-mp="12wditt0pfdcg" />
        <UpcomingPanel cases={data.upcoming.slice(0, 4)} data-id="element-1555" data-mp="xzdd5j7roclv" />
        <InvoicesPanel invoices={data.latestInvoices.slice(0, 4)} titleFor={invoiceTitle} data-id="element-1556" data-mp="oapxw5mog4sj" />
      </div>
    </div>;
}